//dependencies
import React, { PropsWithChildren, ReactNode, useEffect } from "react";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";

//components
import RotatingIconButton from "../Button/templates/RotatingIconButton";
import Panel from "../Panel";
//css
import css from "./ExpandableOnPagePanel.module.scss";

type ExpandableOnPagePanelProps = {
	/**
	 * Whether or not the panel is open
	 */
	isOpen: boolean;
	/**
	 * on Open Click
	 *
	 */
	onOpen?: () => void;
	/**
	 * on Close Click
	 */
	onClose?: () => void;
	/**
	 * Whether or not to use the Panel component
	 */
	usePanel?: boolean;

	/**
	 * List of icons to display in the panel
	 */
	iconList?: ReactNode[];

	/**
	 * testId, the open/close button appends "-button" to this
	 */
	testId?: string;
};

const ExpandableOnPagePanel = ({
	isOpen = false,
	onOpen = () => {},
	onClose = () => {},
	usePanel = false,
	iconList = [],
	children = null,
	testId = "expandable-on-page-panel",
}: PropsWithChildren<ExpandableOnPagePanelProps>) => {
	useEffect(() => {
		if (!isOpen) {
			onClose();
		}
	}, [isOpen]);

	const content = (
		<div
			data-testid={testId}
			data-is-expanded={isOpen}
			className={`group ${css.container} ${usePanel ? css.mobile : ""} ${isOpen ? css.opened : css.closed}`}
		>
			<div className={`${css.iconButtonRow} ${usePanel ? css.mobile : ""} ${isOpen ? css.opened : css.closed}`}>
				<RotatingIconButton
					testId={`${testId}-button`}
					icon={faChevronRight}
					onClick={() => (isOpen ? onClose() : onOpen())}
					isActiveOverride={!isOpen}
					aria-label={isOpen ? "Collapse panel" : "Expand panel"}
				/>
				<div className={`${css.iconButtons} ${isOpen ? css.opened : css.closed}`}>
					{iconList.map((icon) => icon)}
				</div>
			</div>
			<div
				className={`${css.content} ${isOpen ? css.opened : css.closed} group-data-[is-expanded=false]:invisible group-data-[is-expanded=false]:opacity-0 group-data-[is-expanded=true]:visible group-data-[is-expanded=true]:opacity-100 group-data-[is-expanded=true]:transition-opacity group-data-[is-expanded=true]:duration-150`}
			>
				{children}
			</div>
		</div>
	);

	if (usePanel) {
		return (
			<Panel isOpen={isOpen} onClose={onClose} side="right">
				<div
					className="transition-opacity duration-300 ease-in-out data-[is-expanded=false]:opacity-0 data-[is-expanded=true]:opacity-100"
					data-is-expanded={isOpen}
				>
					{content}
				</div>
			</Panel>
		);
	}

	return content;
};

export default ExpandableOnPagePanel;
